import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import Hls from 'hls.js'
import { Maximize, Minimize, Pause, Play, RotateCcw } from 'lucide-react'
import {
  autoLevelCapIndex,
  createPlaybackWindowLoader,
  hlsPlaybackConfig,
  hlsPlaybackProfile,
  isSignedMediaUrl,
  persistBandwidthEstimate,
  readNetworkHint,
  readNetworkTarget,
  readStoredBandwidth,
} from './hlsPlayback'
import { Button } from '@shared/design-system/ui'
import {
  bar,
  bigPlay,
  bigPlayHidden,
  chrome,
  chromeHidden,
  frame,
  frameFlush,
  iconButton,
  overlay,
  played,
  seekBack,
  seekFlash,
  seekForward,
  spacer,
  speedItem,
  speedItemActive,
  speedMenu,
  speedWrap,
  timeLabel,
  track,
  video as videoClass,
  videoInteractive,
} from './hls-video-player.css'

export type HlsVideoPlayerProps = {
  src: string
  title: string
  requestHeaders?: (url: string) => Record<string, string> | undefined
  startAt?: number
  flush?: boolean
  transport?: boolean
  onTime?: (seconds: number, duration: number) => void
  onEnded?: () => void
}

const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const

function formatRate(rate: number) {
  const text = Number.isInteger(rate) ? String(rate) : String(rate).replace('.', ',')
  return `${text}×`
}

function formatClock(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  const padded = String(rest).padStart(2, '0')
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${padded}`
  return `${minutes}:${padded}`
}

const PROGRESS_INTERVAL_MS = 10_000
const CHROME_IDLE_MS = 2_400

export function HlsVideoPlayer({
  src,
  title,
  requestHeaders,
  startAt,
  flush,
  transport: showTransport,
  onTime,
  onEnded,
}: HlsVideoPlayerProps) {
  const frameRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [rate, setRate] = useState<(typeof PLAYBACK_RATES)[number]>(1)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [chromeOn, setChromeOn] = useState(true)
  const [menu, setMenu] = useState(false)
  const [full, setFull] = useState(false)
  const [flash, setFlash] = useState<'back' | 'forward' | null>(null)
  const rateRef = useRef(rate)
  const menuRef = useRef(menu)
  const headersRef = useRef(requestHeaders)
  const onTimeRef = useRef(onTime)
  const startAtRef = useRef(startAt)
  const lastProgressAt = useRef(0)
  const hideTimer = useRef<number | null>(null)
  const clickTimer = useRef<number | null>(null)
  const flashTimer = useRef<number | null>(null)

  useEffect(() => {
    startAtRef.current = startAt
    // A posição seguinte fica com o progresso do player; só o src novo recolhe a prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useEffect(() => {
    headersRef.current = requestHeaders
  }, [requestHeaders])

  useEffect(() => {
    onTimeRef.current = onTime
  }, [onTime])

  useEffect(() => {
    rateRef.current = rate
    if (videoRef.current) videoRef.current.playbackRate = rate
  }, [rate])

  useEffect(() => {
    menuRef.current = menu
  }, [menu])

  useEffect(
    () => () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current)
      if (clickTimer.current) window.clearTimeout(clickTimer.current)
      if (flashTimer.current) window.clearTimeout(flashTimer.current)
    },
    [],
  )

  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === frameRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  function emitProgress(force: boolean) {
    const element = videoRef.current
    const report = onTimeRef.current
    if (!element || !report || !Number.isFinite(element.duration) || element.duration <= 0) return
    const now = Date.now()
    if (!force && now - lastProgressAt.current < PROGRESS_INTERVAL_MS) return
    lastProgressAt.current = now
    report(element.currentTime, element.duration)
  }

  useEffect(() => {
    const element = videoRef.current
    if (!element) return
    setFailed(false)
    const seek = () => {
      element.playbackRate = rateRef.current
      const resumeAt = startAtRef.current
      if (resumeAt && resumeAt > 0 && element.duration > resumeAt) element.currentTime = resumeAt
      setDuration(Number.isFinite(element.duration) ? element.duration : 0)
      setCurrent(element.currentTime)
    }
    element.addEventListener('loadedmetadata', seek, { once: true })

    if (Hls.isSupported()) {
      const profile = hlsPlaybackProfile({
        stored: readStoredBandwidth(window.sessionStorage),
        network: readNetworkHint(),
      })
      const xhrSetup = (xhr: XMLHttpRequest, url: string) => {
        if (isSignedMediaUrl(url)) return
        const headers = headersRef.current?.(url)
        if (!headers) return
        for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)
      }
      const hls = new Hls({
        ...hlsPlaybackConfig(xhrSetup, profile),
        loader: createPlaybackWindowLoader(
          Hls.DefaultConfig.loader as never,
          () => videoRef.current?.currentTime || startAtRef.current || 0,
          xhrSetup,
        ) as never,
      })
      const applyNetworkCap = () => {
        const next = hlsPlaybackProfile({
          stored: readStoredBandwidth(window.sessionStorage),
          network: readNetworkHint(),
        })
        hls.autoLevelCapping = autoLevelCapIndex(hls.levels, next.conservative)
        if (next.conservative) hls.bandwidthEstimate = next.estimateBps
      }
      const connection = readNetworkTarget()
      connection?.addEventListener('change', applyNetworkCap)

      let recovers = 0
      hls.on(Hls.Events.MANIFEST_PARSED, applyNetworkCap)
      hls.on(Hls.Events.FRAG_LOADED, () => {
        persistBandwidthEstimate(window.sessionStorage, hls.bandwidthEstimate)
      })
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return
        if (recovers < 2 && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          recovers += 1
          hls.startLoad()
          return
        }
        if (recovers < 2 && data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          recovers += 1
          hls.recoverMediaError()
          return
        }
        setFailed(true)
      })
      hls.loadSource(src)
      hls.attachMedia(element)
      return () => {
        emitProgress(true)
        if (element.currentTime > 0) startAtRef.current = element.currentTime
        connection?.removeEventListener('change', applyNetworkCap)
        element.removeEventListener('loadedmetadata', seek)
        hls.destroy()
      }
    }

    if (element.canPlayType('application/vnd.apple.mpegurl')) {
      const fail = () => setFailed(true)
      element.addEventListener('error', fail)
      element.src = src
      return () => {
        emitProgress(true)
        if (element.currentTime > 0) startAtRef.current = element.currentTime
        element.removeEventListener('loadedmetadata', seek)
        element.removeEventListener('error', fail)
        element.removeAttribute('src')
        element.load()
      }
    }

    setFailed(true)
    return () => {
      emitProgress(true)
      element.removeEventListener('loadedmetadata', seek)
    }
  }, [src, attempt])

  function skip(seconds: number) {
    const element = videoRef.current
    if (!element || !Number.isFinite(element.duration) || element.duration <= 0) return
    element.currentTime = Math.min(Math.max(0, element.currentTime + seconds), element.duration)
    setCurrent(element.currentTime)
  }

  function showSeek(side: 'back' | 'forward') {
    setFlash(side)
    if (flashTimer.current) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlash(null), 650)
  }

  function revealChrome() {
    setChromeOn(true)
    if (hideTimer.current) window.clearTimeout(hideTimer.current)
    const element = videoRef.current
    if (!element || element.paused || menuRef.current) return
    hideTimer.current = window.setTimeout(() => {
      if (menuRef.current) return
      setChromeOn(false)
    }, CHROME_IDLE_MS)
  }

  function togglePlay() {
    const element = videoRef.current
    if (!element) return
    if (element.paused) {
      const pending = element.play()
      void pending?.catch(() => undefined)
    } else {
      element.pause()
    }
  }

  function toggleFullscreen() {
    const node = frameRef.current
    if (!node) return
    if (document.fullscreenElement === node) {
      void document.exitFullscreen?.()
      return
    }
    void node.requestFullscreen?.()
  }

  function scrub(event: ReactPointerEvent<HTMLDivElement>) {
    const surface = event.currentTarget
    const move = (clientX: number) => {
      const element = videoRef.current
      if (!element || !Number.isFinite(element.duration) || element.duration <= 0) return
      const rect = surface.getBoundingClientRect()
      const ratio = rect.width === 0 ? 0 : Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      element.currentTime = ratio * element.duration
      setCurrent(element.currentTime)
    }
    move(event.clientX)
    const onMove = (ev: PointerEvent) => move(ev.clientX)
    const onUp = () => {
      surface.removeEventListener('pointermove', onMove)
      surface.removeEventListener('pointerup', onUp)
    }
    surface.addEventListener('pointermove', onMove)
    surface.addEventListener('pointerup', onUp)
  }

  function onVideoClick() {
    if (!showTransport) return
    if (clickTimer.current) window.clearTimeout(clickTimer.current)
    clickTimer.current = window.setTimeout(() => {
      clickTimer.current = null
      togglePlay()
    }, 220)
  }

  function onVideoDoubleClick(event: ReactMouseEvent<HTMLVideoElement>) {
    if (!showTransport) return
    if (clickTimer.current) {
      window.clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
    const rect = event.currentTarget.getBoundingClientRect()
    const back = event.clientX - rect.left < rect.width / 2
    skip(back ? -10 : 10)
    showSeek(back ? 'back' : 'forward')
    revealChrome()
  }

  const progress = duration > 0 ? Math.min(100, (current / duration) * 100) : 0
  const stage = (
    <div
      ref={frameRef}
      className={flush ? `${frame} ${frameFlush}` : frame}
      data-testid="hls-video-player"
      onMouseMove={showTransport ? revealChrome : undefined}
      onMouseLeave={showTransport && playing && !menu ? () => setChromeOn(false) : undefined}
      onKeyDown={
        showTransport
          ? (event) => {
              if (event.key === ' ' || event.key === 'k') {
                event.preventDefault()
                togglePlay()
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault()
                skip(-10)
                showSeek('back')
              }
              if (event.key === 'ArrowRight') {
                event.preventDefault()
                skip(10)
                showSeek('forward')
              }
              if (event.key === 'f') toggleFullscreen()
            }
          : undefined
      }
    >
      <video
        ref={videoRef}
        className={showTransport ? `${videoClass} ${videoInteractive}` : videoClass}
        controls={!showTransport}
        playsInline
        preload="metadata"
        aria-label={title}
        controlsList="nodownload"
        disablePictureInPicture
        tabIndex={showTransport ? 0 : undefined}
        onContextMenu={(event) => event.preventDefault()}
        onClick={onVideoClick}
        onDoubleClick={onVideoDoubleClick}
        onPlay={() => {
          setPlaying(true)
          revealChrome()
        }}
        onPause={() => {
          setPlaying(false)
          setChromeOn(true)
          emitProgress(true)
        }}
        onTimeUpdate={() => {
          const element = videoRef.current
          if (element) setCurrent(element.currentTime)
          emitProgress(false)
        }}
        onEnded={() => {
          emitProgress(true)
          onEnded?.()
        }}
      />
      {showTransport && flash ? (
        <div className={`${seekFlash} ${flash === 'back' ? seekBack : seekForward}`} aria-hidden>
          {flash === 'back' ? '−10' : '+10'}
        </div>
      ) : null}
      {showTransport && !failed ? (
        <button
          type="button"
          className={playing ? `${bigPlay} ${bigPlayHidden}` : bigPlay}
          aria-label="Reproduzir"
          tabIndex={playing ? -1 : undefined}
          onClick={togglePlay}
        >
          <Play size={30} fill="currentColor" />
        </button>
      ) : null}
      {showTransport ? (
        <div className={chromeOn || menu ? chrome : `${chrome} ${chromeHidden}`}>
          <div
            className={track}
            role="slider"
            aria-label="Posição do vídeo"
            aria-valuemin={0}
            aria-valuemax={Math.floor(duration)}
            aria-valuenow={Math.floor(current)}
            tabIndex={0}
            onPointerDown={scrub}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === 'ArrowRight') skip(5)
              if (event.key === 'ArrowLeft') skip(-5)
            }}
          >
            <span className={played} style={{ width: `${progress}%` }} />
          </div>
          <div className={bar}>
            <button type="button" className={iconButton} aria-label={playing ? 'Pausar' : 'Reproduzir'} onClick={togglePlay}>
              {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
            </button>
            <button
              type="button"
              className={iconButton}
              aria-label="−10 s"
              onClick={() => {
                skip(-10)
                showSeek('back')
              }}
            >
              −10
            </button>
            <button
              type="button"
              className={iconButton}
              aria-label="+10 s"
              onClick={() => {
                skip(10)
                showSeek('forward')
              }}
            >
              +10
            </button>
            <span className={timeLabel}>
              {formatClock(current)} / {formatClock(duration)}
            </span>
            <span className={spacer} />
            <div className={speedWrap}>
              <button
                type="button"
                className={iconButton}
                aria-label={`Velocidade ${formatRate(rate)}`}
                aria-haspopup="menu"
                aria-expanded={menu}
                onClick={() => {
                  setMenu((open) => !open)
                  setChromeOn(true)
                }}
              >
                {formatRate(rate)}
              </button>
              {menu ? (
                <ul className={speedMenu} role="menu" aria-label="Velocidade">
                  {PLAYBACK_RATES.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={item === rate}
                        className={item === rate ? `${speedItem} ${speedItemActive}` : speedItem}
                        onClick={() => {
                          setRate(item)
                          setMenu(false)
                        }}
                      >
                        {formatRate(item)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <button
              type="button"
              className={iconButton}
              aria-label={full ? 'Sair da tela cheia' : 'Tela cheia'}
              onClick={toggleFullscreen}
            >
              {full ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      ) : null}
      {failed ? (
        <div className={overlay} role="alert">
          <span>Não foi possível carregar o vídeo.</span>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<RotateCcw size={14} />}
            onClick={() => setAttempt((currentAttempt) => currentAttempt + 1)}
          >
            Tentar de novo
          </Button>
        </div>
      ) : null}
    </div>
  )

  return stage
}
